import { useFlag } from '@calendly/feature-check-react'
import { useOnValueChange } from '@calendly/hooks'
import { isEqual } from 'lodash-es'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { useSSEConnectedForPolling } from 'components/meetings'
import { useAdaptivePollingConfig } from 'components/meetings/hooks/use_adaptive_polling_config'
import {
  useGetMeetingsQuery,
  useLazyGetMeetingsQuery,
  useLazyGetMeetingQuery,
} from 'features/meetings/graphql_api'
import { useDebounce } from 'hooks/useDebounce'
import { timezoneSelector } from 'selectors/current_user'
import { useCurrentTime } from 'shared/contexts'

import { useFilterParamsWithUuids, useAncillaryMeetingData } from './hooks'
import { combineMeetings, groupAndMap } from './utils'
import { setTodayQueryArgs } from '../../meetings_page_slice'
import { buildSearchFilters, mapNodeToMeeting } from '../../utils'

const EMPTY_VISIBLE_RANGE = { start: null, end: null }
/** Stable empty object for visible-range query when disabled — avoids new reference each render and duplicate RTK Query subscriptions. */
const EMPTY_QUERY_VARS = {}
/** Page size for visible-range query; additional pages are fetched automatically when there are more. */
const VISIBLE_RANGE_PAGE_SIZE = 100

/**
 * Hook for fetching and managing meeting search data.
 *
 * This hook derives meeting data primarily from RTK Query cache.
 * Mutations (update/cancel) invalidate the cache and queries automatically re-run.
 * Additional pages are fetched lazily and merged with the cache-derived data.
 *
 * @param {Object} options
 * @param {string} options.scopeType - The scope type for filtering
 * @param {string} options.scopeId - The scope ID for filtering
 * @param {boolean} options.showBuffers - Whether to show buffer times
 * @param {boolean} options.skip - Whether to skip fetching data
 * @param {boolean} options.isOrganizer - Whether the user is an organizer
 * @param {boolean} options.includeNotetakerIneligibleExternalEvents - Include external events
 * @param {Object} options.defaultFilters - Default filter parameters
 * @param {Object} options.pollingConfig - Optional. When isPollingEnabled is false (non–realtime path), list and
 *   visible-range polling are disabled regardless of SSE state; caller controls isPollingEnabled and refetchOnFocus.
 * @param {boolean} options.pollingConfig.isPollingEnabled - Enable list + visible-range polling (default: false).
 *   Gate via useMeetingsRealtimeFeaturesEnabled on the legacy page. Polling is paused when the tab is not visible (Page Visibility API).
 * @param {boolean} options.pollingConfig.refetchOnFocus - Refetch when window regains focus (default: false)
 * @param {boolean} options.visibleRangePollingEnabled - When true, run a 4th query for visible date range
 *   (period: fixed). Only set on realtime path.
 * @param {{ start: string | null, end: string | null } | undefined} options.visibleDateRange - Visible range (ISO); debounced 500ms.
 */
export const useSearchData = ({
  scopeType,
  scopeId,
  showBuffers,
  skip = false,
  isOrganizer,
  includeNotetakerIneligibleExternalEvents,
  defaultFilters,
  pollingConfig = {},
  visibleRangePollingEnabled = false,
  visibleDateRange,
}) => {
  const dispatch = useDispatch()
  const { data: currentUser } = useGetUserQuery()
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  // Pagination state for lazy-loaded additional pages
  const [paginatedMeetings, setPaginatedMeetings] = useState([])
  // Track individually refreshed meetings to override cache data
  const [refreshedMeetingsMap, setRefreshedMeetingsMap] = useState({})
  const [nextPagePagination, setNextPagePagination] = useState(null)
  const [previousPagePagination, setPreviousPagePagination] = useState(null)
  // Extra edges from visible-range pagination (when the visible window has more than one page)
  const [visibleRangeExtraEdges, setVisibleRangeExtraEdges] = useState([])
  const visibleRangeNextCursorRef = useRef(null)
  const timezone = useSelector(timezoneSelector)
  const currentTime = useCurrentTime()
  const filterParams = useFilterParamsWithUuids(defaultFilters)

  // Build filters using useMemo instead of useState + useEffect
  const filters = useMemo(() => {
    if (skip) return null
    return buildSearchFilters(
      scopeType,
      scopeId,
      filterParams,
      isOrganizer,
      includeNotetakerIneligibleExternalEvents,
    )
  }, [filterParams, scopeId, scopeType, skip, isOrganizer, includeNotetakerIneligibleExternalEvents])

  const shouldLoadToday = !skip && filterParams?.period === 'upcoming'

  // Track previous filters to reset pagination and refreshed meetings
  const prevFiltersRef = useRef(filters)
  useEffect(() => {
    if (!isEqual(prevFiltersRef.current, filters)) {
      setPaginatedMeetings([])
      setRefreshedMeetingsMap({})
      setNextPagePagination(null)
      setPreviousPagePagination(null)
      prevFiltersRef.current = filters
    }
  }, [filters])

  // Publish today query args to Redux so meeting detail can reuse RTK Query cache.
  useEffect(() => {
    dispatch(setTodayQueryArgs(shouldLoadToday && filters ? { ...filters, today: true } : null))
  }, [dispatch, shouldLoadToday, filters])

  const isPollingEnabled = pollingConfig.isPollingEnabled ?? false
  const isSSEConnectedFromHook = useSSEConnectedForPolling()
  // On non-gated path (isPollingEnabled false), ignore global SSE state so we don't leak realtime behavior.
  const isSSEConnected = isPollingEnabled ? isSSEConnectedFromHook : false
  const { backgroundPollingConfig, fetchOnceNoPollingConfig } = useAdaptivePollingConfig({
    isSSEConnected,
    variant: 'list',
    enabled: isPollingEnabled,
  })

  // Pause polling when tab is hidden (Page Visibility API).
  const [isTabVisible, setIsTabVisible] = useState(
    () => (typeof document !== 'undefined' ? !document.hidden : true)
  )
  useEffect(() => {
    const handler = () => setIsTabVisible(!document.hidden)
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  // When SSE is connected, don't poll today/future/past — rely on SSE invalidations. When not, use background (60s).
  // When tab is hidden, pollingInterval is 0 so we don't poll in the background.
  const queryOptions = useMemo(() => {
    const base = {
      refetchOnMountOrArgChange: true,
      ...(isSSEConnected ? fetchOnceNoPollingConfig : backgroundPollingConfig),
    }
    return { ...base, pollingInterval: isTabVisible ? base.pollingInterval : 0 }
  }, [isSSEConnected, fetchOnceNoPollingConfig, backgroundPollingConfig, isTabVisible])

  const debouncedVisibleDateRange = useDebounce(visibleDateRange || EMPTY_VISIBLE_RANGE, 500)

  const visibleRangeQueryVariables = useMemo(() => {
    if (!visibleRangePollingEnabled || !debouncedVisibleDateRange?.start || !debouncedVisibleDateRange?.end || !filters) {
      return null
    }
    const startMoment = moment(debouncedVisibleDateRange.start)
    const endMoment = moment(debouncedVisibleDateRange.end)
    const rangeDays = endMoment.diff(startMoment, 'days')
    const now = currentTime.clone()

    if (now.diff(endMoment, 'days') > 90) return null

    let queryStart = startMoment
    let queryEnd = endMoment
    if (rangeDays > 7) {
      if (endMoment.isBefore(now, 'day')) {
        queryEnd = endMoment.clone().endOf('day')
        queryStart = queryEnd.clone().subtract(6, 'days').startOf('day')
      } else if (startMoment.isAfter(now, 'day')) {
        queryStart = startMoment.clone().startOf('day')
        queryEnd = queryStart.clone().add(6, 'days').endOf('day')
      } else {
        queryStart = now.clone().subtract(3, 'days').startOf('day')
        queryEnd = now.clone().add(3, 'days').endOf('day')
      }
    }

    return {
      ...filters,
      period: 'fixed',
      startDate: queryStart.format('YYYY-MM-DD'),
      endDate: queryEnd.format('YYYY-MM-DD'),
      order: 'ASC',
      first: VISIBLE_RANGE_PAGE_SIZE,
    }
  }, [
    visibleRangePollingEnabled,
    debouncedVisibleDateRange?.start,
    debouncedVisibleDateRange?.end,
    filters,
    currentTime,
  ])

  // Reset visible-range extra pages when the range or filters change
  const prevVisibleRangeVarsRef = useRef(visibleRangeQueryVariables)
  useEffect(() => {
    if (!isEqual(prevVisibleRangeVarsRef.current, visibleRangeQueryVariables)) {
      setVisibleRangeExtraEdges([])
      visibleRangeNextCursorRef.current = null
      prevVisibleRangeVarsRef.current = visibleRangeQueryVariables
    }
  }, [visibleRangeQueryVariables])

  const skipQueries = skip || !filters

  const { data: todayData, isLoading: todayLoading, isSuccess: todaySuccess } = useGetMeetingsQuery(
    { ...filters, today: true },
    { skip: skipQueries || !shouldLoadToday, ...queryOptions }
  )
  const { data: futureData, isLoading: futureLoading, isSuccess: futureSuccess } = useGetMeetingsQuery(
    { ...filters, order: 'ASC' },
    { skip: skipQueries, ...queryOptions }
  )
  const { data: pastData, isLoading: pastLoading, isSuccess: pastSuccess } = useGetMeetingsQuery(
    { ...filters, past: true, order: 'DESC' },
    { skip: skipQueries, ...queryOptions }
  )

  // Visible range always uses background polling (60s) when tab is visible.
  // SSE invalidations refresh today/future/past; we only poll the visible window.
  // Use stable EMPTY_QUERY_VARS when skipped so RTK Query sees one subscription key, not a new object every render.
  const visibleRangeArgs = visibleRangeQueryVariables ?? EMPTY_QUERY_VARS
  const visibleRangePollingInterval = isTabVisible ? backgroundPollingConfig.pollingInterval : 0
  const { data: visibleRangeData } = useGetMeetingsQuery(visibleRangeArgs, {
    ...backgroundPollingConfig,
    pollingInterval: visibleRangePollingInterval,
    skip: !visibleRangeQueryVariables,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  const [trigger, result] = useLazyGetMeetingsQuery()
  const [triggerVisibleRangeNext, visibleRangeNextResult] = useLazyGetMeetingsQuery()
  const [triggerMeeting] = useLazyGetMeetingQuery()

  // When visible-range first page has more results, fetch next page(s) and accumulate.
  // Only trigger when the first page is full (edges.length >= page size) to avoid a redundant request when total fits in one page.
  const visibleRangeHasNext = visibleRangeData?.meetings?.pageInfo?.hasNextPage
  const visibleRangeEndCursor = visibleRangeData?.meetings?.pageInfo?.endCursor
  const visibleRangeEdgeCount = visibleRangeData?.meetings?.edges?.length ?? 0
  const isFirstPageFull = visibleRangeEdgeCount >= VISIBLE_RANGE_PAGE_SIZE
  useEffect(() => {
    if (!visibleRangeQueryVariables || visibleRangeEndCursor == null) return
    if (!visibleRangeHasNext || !isFirstPageFull || visibleRangeNextResult.isFetching) return
    if (visibleRangeNextCursorRef.current === visibleRangeEndCursor) return
    visibleRangeNextCursorRef.current = visibleRangeEndCursor
    triggerVisibleRangeNext({ ...visibleRangeQueryVariables, after: visibleRangeEndCursor })
  }, [
    visibleRangeQueryVariables,
    visibleRangeHasNext,
    visibleRangeEndCursor,
    isFirstPageFull,
    visibleRangeNextResult.isFetching,
    triggerVisibleRangeNext
  ])

  useOnValueChange(visibleRangeNextResult.status, () => {
    if (visibleRangeNextResult.status !== 'fulfilled' || !visibleRangeNextResult.data?.meetings) return
    if (visibleRangeNextResult.originalArgs?.period !== 'fixed') return
    // Ignore stale next-page result if the visible range changed (when the user scrolls)
    if (
      !visibleRangeQueryVariables
      || visibleRangeNextResult.originalArgs?.startDate !== visibleRangeQueryVariables.startDate
      || visibleRangeNextResult.originalArgs?.endDate !== visibleRangeQueryVariables.endDate
    ) {
      return
    }
    const { edges, pageInfo } = visibleRangeNextResult.data.meetings
    setVisibleRangeExtraEdges((prev) => [...prev, edges])
    if (pageInfo?.hasNextPage && pageInfo?.endCursor && visibleRangeNextResult.originalArgs) {
      visibleRangeNextCursorRef.current = pageInfo.endCursor
      triggerVisibleRangeNext({
        ...visibleRangeNextResult.originalArgs,
        after: pageInfo.endCursor,
      })
    } else {
      visibleRangeNextCursorRef.current = null
    }
  })

  // Derive meetings directly from RTK Query cache (no useState for base data).
  // Guards use isLoading (first fetch only) so polling refetches don't wipe data.
  const baseMeetings = useMemo(() => {
    if (skip || !filters) return []

    if (futureLoading || pastLoading || (shouldLoadToday && todayLoading)) return []

    let combined = []
    const edgesFuture = futureData?.meetings?.edges
    const edgesToday = todayData?.meetings?.edges
    const edgesPast = pastData?.meetings?.edges
    const edgesVisibleRangeFirst = visibleRangeData?.meetings?.edges ?? []
    const allVisibleRangeEdges = visibleRangeExtraEdges.length
      ? [...edgesVisibleRangeFirst, ...visibleRangeExtraEdges.flat()]
      : edgesVisibleRangeFirst

    if (edgesFuture) {
      combined = combineMeetings(combined, groupAndMap(edgesFuture, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    if (shouldLoadToday && edgesToday) {
      combined = combineMeetings(combined, groupAndMap(edgesToday, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    if (edgesPast) {
      combined = combineMeetings(combined, groupAndMap(edgesPast, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    if (allVisibleRangeEdges.length) {
      combined = combineMeetings(combined, groupAndMap(allVisibleRangeEdges, timezone), currentUser?.uuid)
    }

    return combined
  }, [
    skip,
    filters,
    futureData?.meetings?.edges,
    futureLoading,
    todayData?.meetings?.edges,
    todayLoading,
    pastData?.meetings?.edges,
    pastLoading,
    shouldLoadToday,
    visibleRangeData?.meetings?.edges,
    visibleRangeExtraEdges,
    timezone,
    hasMsTeamsFlag,
    currentUser?.uuid,
  ])

  // Combine base meetings with paginated meetings, applying any refreshed meeting overrides
  const meetings = useMemo(() => {
    let combined = baseMeetings
    if (paginatedMeetings.length > 0) {
      combined = combineMeetings(combined, paginatedMeetings, currentUser?.uuid)
    }

    // Apply refreshed meetings overrides
    if (Object.keys(refreshedMeetingsMap).length > 0) {
      combined = combined.map((dayGroup) => ({
        ...dayGroup,
        events: dayGroup.events.map((event) => (refreshedMeetingsMap[event.uuid]
          ? { ...refreshedMeetingsMap[event.uuid], source: refreshedMeetingsMap[event.uuid].source ?? event.source }
          : event)
        )
      }))
    }

    return combined
  }, [baseMeetings, paginatedMeetings, refreshedMeetingsMap, currentUser?.uuid])

  // Update pagination cursors when initial data loads
  useEffect(() => {
    if (futureData?.meetings?.pageInfo) {
      if (futureData.meetings.pageInfo.hasNextPage) {
        setNextPagePagination(futureData.meetings.pageInfo.endCursor)
      } else {
        setNextPagePagination(null)
      }
    }
  }, [futureData?.meetings?.pageInfo])

  useEffect(() => {
    if (pastData?.meetings?.pageInfo) {
      const pageInfo = pastData.meetings.pageInfo
      if (pageInfo.hasNextPage && pageInfo.endCursor) {
        setPreviousPagePagination(pageInfo.endCursor)
      } else {
        setPreviousPagePagination(null)
      }
    }
  }, [pastData?.meetings?.pageInfo])

  const isPreviousPage = useCallback((args) => args?.isPrevious || args?.past, [])
  useOnValueChange(result.status, () => {
    if (!skip && result.status === 'fulfilled' && result.data?.meetings) {
      const pageInfo = result.data.meetings.pageInfo
      const hasMore = pageInfo.hasNextPage
      const cursor = pageInfo.endCursor
      if (hasMore && cursor) {
        if (isPreviousPage(result.originalArgs)) {
          setPreviousPagePagination(cursor)
        } else {
          setNextPagePagination(cursor)
        }
      } else if (isPreviousPage(result.originalArgs)) {
        setPreviousPagePagination(null)
      } else {
        setNextPagePagination(null)
      }
      // Only paginated data needs useState - it's not part of the initial cache
      setPaginatedMeetings((current) => combineMeetings(current, groupAndMap(result.data.meetings.edges, timezone, hasMsTeamsFlag))
      )
    }
  })

  const isLoading = useMemo(() => {
    if (skip) return false
    return futureLoading || pastLoading || (shouldLoadToday && todayLoading)
  }, [skip, shouldLoadToday, futureLoading, pastLoading, todayLoading])

  // Apply ancillary data transformations
  const [updateMeetings, { isLoading: ancillaryDataLoading }] = useAncillaryMeetingData({ meetings, showBuffers })

  const retrieveNextPage = useCallback(() => {
    if (!skip && nextPagePagination && !result.isFetching && filters) {
      trigger({ ...filters, after: nextPagePagination, order: 'ASC' })
    }
  }, [nextPagePagination, result.isFetching, trigger, filters, skip])

  const retrievePreviousPage = useCallback(() => {
    if (!skip && previousPagePagination && !result.isFetching && filters) {
      return trigger({
        ...filters,
        order: 'DESC',
        past: true,
        after: previousPagePagination,
        isPrevious: true,
      })
    }
    return Promise.resolve(null)
  }, [previousPagePagination, result.isFetching, trigger, filters, skip])

  const refreshMeeting = useCallback(async (uuid) => {
    if (skip || !uuid) {
      return
    }

    const queryFilters = filters ? { ...filters } : {}
    const response = await triggerMeeting({ ...queryFilters, uuid, _t: Date.now() })
    if (!response?.data) {
      return
    }

    const refreshedMeeting = mapNodeToMeeting(response.data, timezone, hasMsTeamsFlag)
    if (!refreshedMeeting?.uuid) return
    // Store refreshed meeting in map to override cache data
    setRefreshedMeetingsMap((prev) => ({
      ...prev,
      [refreshedMeeting.uuid]: refreshedMeeting
    }))
  }, [filters, skip, timezone, hasMsTeamsFlag, triggerMeeting])

  const isSuccess = useMemo(
    () => {
      if (shouldLoadToday) {
        return futureSuccess && pastSuccess && todaySuccess
      }
      return futureSuccess && pastSuccess
    },
    [futureSuccess, pastSuccess, todaySuccess, shouldLoadToday]
  )


  return useMemo(() => {
    if (skip) {
      return null
    }

    return {
      data: updateMeetings(meetings),
      isSuccess,
      isLoading: isLoading || (ancillaryDataLoading && meetings.length === 0),
      retrieveNextPage,
      hasNext: !!nextPagePagination,
      isNextLoading: result?.isFetching && !result?.originalArgs?.past,
      retrievePreviousPage,
      hasPrevious: !!previousPagePagination,
      isPreviousLoading: result?.isFetching && !!result?.originalArgs?.past,
      refreshMeeting
    }
  }, [
    skip,
    updateMeetings,
    meetings,
    isSuccess,
    isLoading,
    ancillaryDataLoading,
    retrieveNextPage,
    nextPagePagination,
    result,
    retrievePreviousPage,
    previousPagePagination,
    refreshMeeting,
  ])
}
